import React from "react"
import { Typography } from "antd"
import { useOutletContext } from "react-router-dom"

import { Value } from "@components/Text"
import { TRANSACTION_STATUS } from "@components/Domain"
import { getUsDateFromIsoDate } from "@components/Date"
import {
  TransactionStatusTag,
  getTransactionTypeLabel,
  getTransactionStatusLabel,
} from "@components/Transaction"

import getAmountColumnProps from "./getAmountColumnProps"
import useLiaisonColumnProps from "./useLiaisonColumnProps"

const { Text, Link: TextLink } = Typography

const LABEL_PROFILE = "Profile"
const LABEL_PROJECT = "Project"
const LABEL_SPONSOR = "Sponsor"
const LABEL_STATUS = "Status"


const renderRejected = (status, value) => {
  const isRejected = status === TRANSACTION_STATUS.REJECTED

  return (
    <Text delete={isRejected}>
      {value}
    </Text>
  )
}


const useColumnsConfig = (
  openInvestmentDrawer,
  openTransactionDrawer
) => {
  const {
    organization,
    getProjectName,
    getSponsorName,
    hasMultipleSponsors,
    isInvalidBankAccountTransaction,
  } = useOutletContext()

  const { name: organizationName } = organization

  const liaisonColumnProps = useLiaisonColumnProps(renderRejected)

  const dateColumnProps = {
    key: "date",
    render: (date, { status }) =>
      renderRejected(status, <Value value={getUsDateFromIsoDate(date)} />),
  }

  const investmentNameColumnProps = {
    key: "investmentName",
    title: LABEL_PROFILE,
    render: (value, { status, investmentId }) =>
      renderRejected(status,
        <TextLink strong onClick={() => openInvestmentDrawer(investmentId)}>
          {value}
        </TextLink>
      ),
    isSearchable: true,
  }

  const projectColumnProps = {
    key: "projectId",
    title: LABEL_PROJECT,
    compute: ({ projectId }) => getProjectName(projectId),
    isFilterEnabled: true
  }

  const sponsorColumnProps = {
    key: "sponsorId",
    title: LABEL_SPONSOR,
    compute: ({ sponsorId }) => getSponsorName(sponsorId),
    isFilterEnabled: true
  }

  const typeColumnProps = {
    key: "type",
    compute: ({ type }) => getTransactionTypeLabel(type),
    isFilterEnabled: true
  }

  const amountInColumnProps = getAmountColumnProps(organizationName, openTransactionDrawer, isInvalidBankAccountTransaction)
  const amountOutColumnProps = getAmountColumnProps(organizationName, openTransactionDrawer, isInvalidBankAccountTransaction, false)

  const statusColumnProps = {
    key: "_computedStatus",
    title: LABEL_STATUS,
    align: "right",
    compute: ({ _computedStatus }) => getTransactionStatusLabel(_computedStatus),
    render: (label, transaction) => <TransactionStatusTag transaction={transaction} />,
    isFilterEnabled: true,
  }

  return [
    { ...dateColumnProps, fixed: "left", width: 100 },
    { ...investmentNameColumnProps, fixed: "left", width: 300, hasTruncation: true },
    { ...projectColumnProps, width: "auto" },
    ...(
      hasMultipleSponsors
        ? [{ ...sponsorColumnProps, width: "auto" }]
        : []
    ),
    { ...liaisonColumnProps, width: "auto", widthAutoPadding: 40 },
    { ...typeColumnProps, width: "auto" },
    { ...amountInColumnProps, width: 140 },
    { ...amountOutColumnProps, width: 140 },
    { ...statusColumnProps, width: 140 },
  ]
}

export default useColumnsConfig
